/*
 * UGC Infinite Wall v2
 * - Single row of posters. Zero <video> elements until the user taps.
 * - Native horizontal scrolling (browser handles touch physics and axis
 *   locking, so vertical page scroll is never hijacked) plus a gentle
 *   auto-drift that pauses while the visitor interacts.
 * - Tapping a tile opens a fullscreen vertical reels viewer (scroll-snap,
 *   swipe up for the next video) with sound, a swipe hint and a close X.
 * - One reused <video> element in the viewer keeps sound working across
 *   slides on iOS (the element stays gesture-activated after src changes).
 */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  var COARSE = window.matchMedia('(pointer: coarse)');

  var ICON_PLAY = '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 5V19L19 12L8 5Z" fill="currentColor"/></svg>';
  var ICON_CLOSE = '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 6L18 18M18 6L6 18" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/></svg>';
  var ICON_CHEVRON_UP = '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 15L12 9L18 15" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var ICON_SOUND = '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 9V15H8L13 20V4L8 9H4Z" fill="currentColor"/><path d="M16 8.5C17.3 9.6 17.3 14.4 16 15.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><path d="M18.5 6C21 8.2 21 15.8 18.5 18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';

  function initSection(root) {
    if (!root || root.dataset.ugcInit === '1') return;
    root.dataset.ugcInit = '1';

    var scroller = root.querySelector('[data-ugc-scroller]');
    var track = root.querySelector('[data-ugc-track]');
    if (!scroller || !track) return;

    var items = [];
    var itemsEl = root.querySelector('[data-ugc-items]');
    if (itemsEl) {
      try { items = JSON.parse(itemsEl.textContent) || []; } catch (e) { items = []; }
    }

    var speed = parseFloat(root.dataset.ugcSpeed);
    if (isNaN(speed)) speed = 30;

    /* ---------- Infinite loop + auto-drift ---------- */

    var L = 0;              // stride: width of one content copy + gap
    var copiesMade = 1;
    var loopReady = false;
    var pos = 0;            // float mirror of scrollLeft during drift
    var expected = -1;      // last programmatic scrollLeft (user-scroll detection)
    var lastInteract = 0;
    var touchActive = false;
    var hoverPause = false;
    var mouseDragging = false;
    var visible = false;
    var viewerOpen = false;
    var lastT = 0;

    var vis = new IntersectionObserver(function (entries) {
      visible = entries[0].isIntersecting;
    }, { rootMargin: '80px' });
    vis.observe(scroller);

    // While the PAGE is scrolling, per-frame scrollLeft writes fight the
    // compositor and the drift visibly stutters — hold it until the page
    // settles.
    var lastPageScroll = 0;
    window.addEventListener('scroll', function () {
      lastPageScroll = performance.now();
    }, { passive: true });

    function setupLoop(tries) {
      if (!root.isConnected) return;
      var baseWidth = track.scrollWidth;
      if (baseWidth < 10) {
        if (tries < 60) requestAnimationFrame(function () { setupLoop(tries + 1); });
        return;
      }
      // Not enough content to scroll — nothing to loop.
      if (baseWidth <= scroller.clientWidth * 1.2) return;

      var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
      L = baseWidth + gap;

      var originals = Array.prototype.slice.call(track.children);
      // Enough copies that a hard fling (~3800px) from the middle copy never
      // reaches a physical edge mid-momentum.
      var copies = Math.max(
        5,
        Math.ceil((scroller.clientWidth * 1.5) / L) + 2,
        Math.ceil(3800 / L) + 3
      );
      copiesMade = copies;
      for (var c = 1; c < copies; c++) {
        var frag = document.createDocumentFragment();
        originals.forEach(function (el) {
          var clone = el.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.removeAttribute('data-shopify-editor-block');
          clone.querySelectorAll('button').forEach(function (b) { b.tabIndex = -1; });
          frag.appendChild(clone);
        });
        track.appendChild(frag);
      }

      pos = L * 2; // rest in the middle copy — fling headroom both ways
      scroller.scrollLeft = pos;
      expected = scroller.scrollLeft;
      loopReady = true;
      requestAnimationFrame(tick);
    }

    function driftable() {
      var now = performance.now();
      return loopReady && speed > 0 && visible && !viewerOpen && !touchActive &&
        !hoverPause && !mouseDragging && !document.hidden && !REDUCED.matches &&
        !document.body.classList.contains('ugc-viewer-open') &&
        (now - lastPageScroll > 300) &&
        (now - lastInteract > 2000);
    }

    function tick(t) {
      if (!root.isConnected) return;
      if (driftable()) {
        if (!lastT) lastT = t;
        var dt = Math.min((t - lastT) / 1000, 0.1);
        lastT = t;
        pos += speed * dt;
        if (pos >= L * 3) pos -= L;
        scroller.scrollLeft = pos;
        expected = scroller.scrollLeft;
        requestAnimationFrame(tick);
      } else {
        lastT = 0;
        setTimeout(function () { requestAnimationFrame(tick); }, 350);
      }
    }

    var idleTimer = 0;
    scroller.addEventListener('scroll', function () {
      var sl = scroller.scrollLeft;
      if (Math.abs(sl - expected) > 1.5) {
        lastInteract = performance.now();
        pos = sl;
      }
      if (!loopReady) return;
      var max = scroller.scrollWidth - scroller.clientWidth;
      // Emergency wrap only right at the physical edges. It cancels iOS
      // momentum, but that beats rubber-banding at the end of the track.
      if (sl < L * 0.15) {
        scroller.scrollLeft = sl + L;
        pos = scroller.scrollLeft;
        expected = pos;
      } else if (sl > max - L * 0.15) {
        scroller.scrollLeft = sl - L;
        pos = scroller.scrollLeft;
        expected = pos;
      }
      // Normal wrap happens lazily once scrolling settles, so native
      // momentum is never interrupted. Re-centre into the middle copy
      // [2L, 3L) — the jump is an exact multiple of L, so it's invisible.
      clearTimeout(idleTimer);
      idleTimer = setTimeout(function () {
        if (!root.isConnected) return;
        var s2 = scroller.scrollLeft;
        if (s2 >= L * 2 && s2 < L * 3) return;
        var norm = L * 2 + (((s2 - L * 2) % L) + L) % L;
        scroller.scrollLeft = norm;
        pos = norm;
        expected = norm;
      }, 160);
    }, { passive: true });

    scroller.addEventListener('touchstart', function () {
      touchActive = true;
      lastInteract = performance.now();
    }, { passive: true });
    scroller.addEventListener('touchend', function () {
      touchActive = false;
      lastInteract = performance.now();
    }, { passive: true });
    scroller.addEventListener('touchcancel', function () {
      touchActive = false;
      lastInteract = performance.now();
    }, { passive: true });
    scroller.addEventListener('wheel', function () {
      lastInteract = performance.now();
    }, { passive: true });
    // Only real hover devices — mobile browsers fire synthetic mouseenter on
    // tap and never mouseleave, which would park the drift forever.
    if (window.matchMedia('(hover: hover)').matches) {
      scroller.addEventListener('mouseenter', function () { hoverPause = true; });
      scroller.addEventListener('mouseleave', function () { hoverPause = false; });
    }
    scroller.addEventListener('dragstart', function (e) { e.preventDefault(); });

    // Tile widths change at the 750px breakpoint (rotation, window resize):
    // recompute the loop stride from the cloned track.
    window.addEventListener('resize', function () {
      if (!loopReady || !root.isConnected) return;
      var g = parseFloat(getComputedStyle(track).columnGap) || 0;
      L = (track.scrollWidth + g) / copiesMade;
    });

    /* ---------- Mouse drag-to-scroll (desktop) ---------- */

    var mouseDown = false;
    var dragStartX = 0;
    var dragStartSL = 0;
    var suppressClick = false;

    scroller.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      mouseDown = true;
      mouseDragging = false;
      dragStartX = e.clientX;
      dragStartSL = scroller.scrollLeft;
      // No pointer capture here — capturing on pointerdown retargets the
      // subsequent click to the scroller, so tiles would never open.
    });
    scroller.addEventListener('pointermove', function (e) {
      if (e.pointerType !== 'mouse' || !mouseDown) return;
      var dx = e.clientX - dragStartX;
      if (!mouseDragging && Math.abs(dx) > 6) {
        mouseDragging = true;
        scroller.classList.add('is-dragging');
        try { scroller.setPointerCapture(e.pointerId); } catch (err) {}
      }
      if (mouseDragging) {
        scroller.scrollLeft = dragStartSL - dx;
        lastInteract = performance.now();
      }
    });
    function endMouseDrag() {
      if (!mouseDown) return;
      mouseDown = false;
      if (mouseDragging) {
        suppressClick = true;
        setTimeout(function () { suppressClick = false; }, 0);
      }
      mouseDragging = false;
      scroller.classList.remove('is-dragging');
      lastInteract = performance.now();
    }
    scroller.addEventListener('pointerup', endMouseDrag);
    scroller.addEventListener('pointercancel', endMouseDrag);
    window.addEventListener('pointerup', endMouseDrag);

    /* ---------- Open viewer ---------- */

    scroller.addEventListener('click', function (e) {
      if (suppressClick) {
        suppressClick = false;
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      var btn = e.target.closest('[data-ugc-open]');
      if (!btn) return;
      var idx = parseInt(btn.getAttribute('data-ugc-open'), 10);
      if (isNaN(idx) || !items[idx]) return;
      openViewer(idx, btn);
    }, true);

    /* ---------- Fullscreen reels viewer ---------- */

    var viewerEl = null;
    var feed = null;
    var slides = [];
    var mainVideo = null;
    var preloader = null;
    var activeIdx = -1;
    var soundOn = true;
    var pushed = false;
    var originBtn = null;
    var io = null;
    var savedScrollY = 0;
    var soundPill = null;
    var closeBtnRef = null;
    var hintEl = null;
    var hintTimer = 0;
    var hintHideHandler = null;
    var nudging = false;
    var nudgeTimer = 0;

    function lockBody() {
      savedScrollY = window.scrollY || window.pageYOffset || 0;
      document.body.classList.add('ugc-viewer-open');
      document.body.style.position = 'fixed';
      document.body.style.top = -savedScrollY + 'px';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    }

    function restoreScroll(y) {
      try { window.scrollTo({ top: y, left: 0, behavior: 'instant' }); }
      catch (e) { window.scrollTo(0, y); }
    }

    function unlockBody() {
      document.body.classList.remove('ugc-viewer-open');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      restoreScroll(savedScrollY);
    }

    function attemptPlay() {
      if (!mainVideo) return;
      var v = mainVideo;
      var p = v.play();
      if (p && p.catch) {
        p.catch(function (err) {
          if (!mainVideo || v !== mainVideo) return;
          // AbortError just means a src change or pause() superseded this
          // play() — it is NOT an autoplay block. Don't touch sound state.
          if (err && err.name === 'AbortError') return;
          if (!v.muted) {
            soundOn = false;
            v.muted = true;
            if (soundPill) soundPill.classList.add('is-visible');
            attemptPlay();
          } else {
            // Even muted play was refused (e.g. iOS Low Power Mode outside a
            // gesture) — show the play affordance instead of an endless spinner.
            var slide = slides[activeIdx];
            if (slide) slide.classList.add('is-paused');
          }
        });
      }
    }

    function activate(idx) {
      if (!viewerEl || idx === activeIdx || !slides[idx]) return;
      var prev = slides[activeIdx];
      if (prev) prev.classList.remove('is-active', 'is-live', 'is-paused', 'is-buffering');
      activeIdx = idx;
      var slide = slides[idx];
      slide.classList.add('is-active');
      slide.classList.remove('is-live', 'is-paused', 'is-buffering');

      slide.querySelector('.ugc-viewer-media').appendChild(mainVideo);
      var it = items[idx];
      if (mainVideo.dataset.url !== it.url) {
        mainVideo.dataset.url = it.url;
        mainVideo.src = it.url;
      } else {
        try { mainVideo.currentTime = 0; } catch (e) { /* not seekable yet */ }
      }
      mainVideo.muted = !soundOn;
      attemptPlay();
      scheduleNextPreload(idx);
    }

    var pendingPreload = null;
    function scheduleNextPreload(idx) {
      if (!mainVideo || !preloader) return;
      if (pendingPreload) {
        mainVideo.removeEventListener('canplaythrough', pendingPreload);
        pendingPreload = null;
      }
      var conn = navigator.connection;
      if (conn && conn.saveData) return;
      var next = items[idx + 1] || items[idx - 1];
      if (!next || preloader.dataset.url === next.url) return;
      // Only start buffering the next clip once the current one is
      // comfortably ahead — never compete with the playing video.
      var onReady = function () {
        if (mainVideo) mainVideo.removeEventListener('canplaythrough', onReady);
        pendingPreload = null;
        if (!preloader || activeIdx !== idx) return;
        preloader.dataset.url = next.url;
        preloader.src = next.url;
      };
      pendingPreload = onReady;
      mainVideo.addEventListener('canplaythrough', onReady);
    }

    function hideHint() {
      if (hintEl) hintEl.classList.add('is-hidden');
      if (hintTimer) { clearTimeout(hintTimer); hintTimer = 0; }
      if (feed && hintHideHandler) {
        feed.removeEventListener('scroll', hintHideHandler);
        hintHideHandler = null;
      }
    }

    // On open, briefly peek the next slide and settle back — a visual cue
    // that the feed scrolls, alongside the arrow hint.
    function nudgeFeed() {
      if (!feed || REDUCED.matches || nudging) return;
      if (activeIdx >= items.length - 1) return;
      var base = activeIdx * feed.clientHeight;
      if (Math.abs(feed.scrollTop - base) > 2) return; // user already moved
      var PEEK = Math.min(72, feed.clientHeight * 0.1);
      var DUR = 850;
      var start = 0;
      nudging = true;
      var cancel = function () { nudging = false; };
      feed.addEventListener('touchstart', cancel, { passive: true, once: true });
      feed.addEventListener('wheel', cancel, { passive: true, once: true });
      requestAnimationFrame(function step(t) {
        if (!feed) return;
        if (!nudging) return;
        if (!start) start = t;
        var p = Math.min((t - start) / DUR, 1);
        feed.scrollTop = base + Math.sin(p * Math.PI) * PEEK;
        if (p < 1) {
          requestAnimationFrame(step);
        } else {
          feed.scrollTop = base;
          // Scroll events from the last writes land AFTER this frame — keep
          // the flag up briefly so they don't dismiss the hint.
          setTimeout(function () { nudging = false; }, 150);
        }
      });
    }

    function onKey(e) {
      if (e.key === 'Escape') {
        closeViewer(false);
      } else if (e.key === 'ArrowDown' || e.key === 'PageDown') {
        e.preventDefault();
        feed.scrollBy({ top: feed.clientHeight, behavior: 'smooth' });
      } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault();
        feed.scrollBy({ top: -feed.clientHeight, behavior: 'smooth' });
      } else if (e.key === 'Tab') {
        // Keep focus inside the modal — the page behind is visually hidden
        // but still focusable otherwise.
        var focusables = [];
        if (closeBtnRef) focusables.push(closeBtnRef);
        if (soundPill && soundPill.classList.contains('is-visible')) focusables.push(soundPill);
        if (!focusables.length) return;
        e.preventDefault();
        var i = focusables.indexOf(document.activeElement);
        var n = e.shiftKey
          ? (i <= 0 ? focusables.length - 1 : i - 1)
          : (i === -1 || i === focusables.length - 1 ? 0 : i + 1);
        focusables[n].focus();
      }
    }

    function onPop() {
      pushed = false;
      closeViewer(true);
    }

    var resizeRaf = 0;
    function onResize() {
      if (resizeRaf) return;
      resizeRaf = requestAnimationFrame(function () {
        resizeRaf = 0;
        if (feed && activeIdx >= 0) feed.scrollTop = activeIdx * feed.clientHeight;
      });
    }

    function openViewer(startIdx, btn) {
      if (viewerOpen || !items.length) return;
      viewerOpen = true;
      originBtn = btn || null;

      viewerEl = document.createElement('div');
      viewerEl.className = 'ugc-viewer';
      viewerEl.setAttribute('role', 'dialog');
      viewerEl.setAttribute('aria-modal', 'true');
      viewerEl.setAttribute('aria-label', 'Customer videos');

      feed = document.createElement('div');
      feed.className = 'ugc-viewer-feed';

      slides = items.map(function (it, i) {
        var slide = document.createElement('div');
        slide.className = 'ugc-viewer-slide';
        slide.dataset.idx = i;

        var media = document.createElement('div');
        media.className = 'ugc-viewer-media';
        if (it.poster) {
          var img = document.createElement('img');
          img.className = 'ugc-viewer-poster';
          img.alt = '';
          img.loading = Math.abs(i - startIdx) <= 1 ? 'eager' : 'lazy';
          img.src = it.poster;
          media.appendChild(img);
        }
        var spinner = document.createElement('div');
        spinner.className = 'ugc-viewer-spinner';
        media.appendChild(spinner);

        var state = document.createElement('div');
        state.className = 'ugc-viewer-state';
        state.innerHTML = ICON_PLAY;
        media.appendChild(state);

        slide.appendChild(media);

        if (it.caption) {
          var cap = document.createElement('div');
          cap.className = 'ugc-viewer-caption';
          cap.textContent = it.caption;
          slide.appendChild(cap);
        }
        feed.appendChild(slide);
        return slide;
      });

      var closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'ugc-viewer-close';
      closeBtn.setAttribute('aria-label', 'Close videos');
      closeBtn.innerHTML = ICON_CLOSE;
      closeBtn.addEventListener('click', function () { closeViewer(false); });
      closeBtnRef = closeBtn;

      soundPill = document.createElement('button');
      soundPill.type = 'button';
      soundPill.className = 'ugc-viewer-sound';
      soundPill.innerHTML = ICON_SOUND + '<span>Tap for sound</span>';
      soundPill.addEventListener('click', function (e) {
        e.stopPropagation();
        soundOn = true;
        if (mainVideo) {
          mainVideo.muted = false;
          if (mainVideo.paused) attemptPlay();
        }
        soundPill.classList.remove('is-visible');
      });
      // Sound was blocked in an earlier session of the viewer — surface the
      // pill straight away instead of opening permanently silent.
      if (!soundOn) soundPill.classList.add('is-visible');

      viewerEl.appendChild(feed);
      viewerEl.appendChild(closeBtn);
      viewerEl.appendChild(soundPill);

      if (items.length > 1) {
        hintEl = document.createElement('div');
        hintEl.className = 'ugc-viewer-hint';
        hintEl.innerHTML = ICON_CHEVRON_UP + '<span>' +
          (COARSE.matches ? 'Swipe up for more' : 'Scroll for more') + '</span>';
        viewerEl.appendChild(hintEl);
        hintTimer = setTimeout(hideHint, 6000);
        // Hide once the USER scrolls — the automatic peek nudge shouldn't
        // dismiss it.
        hintHideHandler = function () { if (!nudging) hideHint(); };
        feed.addEventListener('scroll', hintHideHandler, { passive: true });
        nudgeTimer = setTimeout(function () {
          nudgeTimer = 0;
          if (viewerEl) nudgeFeed();
        }, 700);
      }

      // Tap video to pause / resume
      feed.addEventListener('click', function () {
        if (!mainVideo || activeIdx < 0) return;
        var slide = slides[activeIdx];
        if (mainVideo.paused) {
          slide.classList.remove('is-paused');
          attemptPlay();
        } else {
          mainVideo.pause();
          slide.classList.add('is-paused');
        }
      });

      document.body.appendChild(viewerEl);
      lockBody();

      // One reused, gesture-activated video element — keeps sound alive
      // across slides on iOS.
      mainVideo = document.createElement('video');
      mainVideo.className = 'ugc-viewer-video';
      mainVideo.playsInline = true;
      mainVideo.setAttribute('playsinline', '');
      mainVideo.loop = true;
      mainVideo.preload = 'auto';
      mainVideo.addEventListener('playing', function () {
        var slide = slides[activeIdx];
        if (slide) {
          slide.classList.add('is-live');
          slide.classList.remove('is-buffering');
        }
      });
      // Mid-playback stall (slow connection) — surface the loader again.
      mainVideo.addEventListener('waiting', function () {
        var slide = slides[activeIdx];
        if (slide) slide.classList.add('is-buffering');
      });

      preloader = document.createElement('video');
      preloader.muted = true;
      preloader.playsInline = true;
      preloader.preload = 'auto';

      feed.scrollTop = startIdx * feed.clientHeight;
      activate(startIdx);

      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) {
            activate(parseInt(en.target.dataset.idx, 10));
          }
        });
      }, { root: feed, threshold: 0.6 });
      slides.forEach(function (s) { io.observe(s); });

      document.addEventListener('keydown', onKey);
      window.addEventListener('resize', onResize);
      try {
        // The body lock collapses the document, so the browser records
        // scroll=0 against the page's history entry at pushState time. With
        // 'auto' restoration, closing via history.back() would then snap the
        // page to the top — take over scroll restoration while we're open.
        if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
        history.pushState({ ugcReels: true }, '');
        pushed = true;
        window.addEventListener('popstate', onPop);
      } catch (e) { pushed = false; }

      closeBtn.focus({ preventScroll: true });
    }

    function closeViewer(fromHistory) {
      if (!viewerEl) return;
      if (io) { io.disconnect(); io = null; }
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('popstate', onPop);
      if (hintTimer) { clearTimeout(hintTimer); hintTimer = 0; }
      if (nudgeTimer) { clearTimeout(nudgeTimer); nudgeTimer = 0; }
      nudging = false;
      hintHideHandler = null;

      if (mainVideo) {
        mainVideo.pause();
        mainVideo.removeAttribute('src');
        try { mainVideo.load(); } catch (e) {}
        mainVideo = null;
      }
      if (preloader) {
        preloader.removeAttribute('src');
        try { preloader.load(); } catch (e) {}
        preloader = null;
      }

      viewerEl.remove();
      viewerEl = null;
      feed = null;
      slides = [];
      activeIdx = -1;
      hintEl = null;
      soundPill = null;
      closeBtnRef = null;
      pendingPreload = null;

      unlockBody();
      viewerOpen = false;
      lastInteract = performance.now();

      if (originBtn && originBtn.isConnected) {
        try { originBtn.focus({ preventScroll: true }); } catch (e) {}
      }
      originBtn = null;

      if (!fromHistory && pushed) {
        pushed = false;
        history.back();
      }
      // Re-assert the position once the back-navigation settles, then hand
      // scroll restoration back to the browser.
      var y = savedScrollY;
      setTimeout(function () {
        restoreScroll(y);
        if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
      }, 80);
    }

    requestAnimationFrame(function () { setupLoop(0); });
  }

  function boot() {
    document.querySelectorAll('[data-ugc-wall]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target && e.target.querySelector && e.target.querySelector('[data-ugc-wall]');
    if (el) initSection(el);
  });
})();
